// @flow
// One publisher for "the address bar may have changed": popstate plus
// history.pushState/replaceState, which fire no event of their own.
//
// history is wrapped once, however many links are mounted, and the originals
// are put back when the last subscriber leaves. The document survives a Turbo
// visit, so a wrapper per mount would pile up for as long as the tab is open.

type Listener = () => void

const listeners: Set<Listener> = new Set()
let restore: ?() => void = null

const notify = () => {
  // A listener may unsubscribe while being notified.
  Array.from(listeners).forEach((listener) => listener())
}

const wrap = (method: 'pushState' | 'replaceState') => {
  const original = window.history[method]
  const wrapper = (...args) => {
    original.apply(window.history, args)
    notify()
  }
  window.history[method] = wrapper

  return () => {
    // Someone else wrapped history after us: taking ours out would drop theirs.
    // Ours stays in the chain and notifies nobody.
    if (window.history[method] === wrapper)
      window.history[method] = original
  }
}

const install = () => {
  const restorePushState = wrap('pushState')
  const restoreReplaceState = wrap('replaceState')
  window.addEventListener('popstate', notify)

  restore = () => {
    restorePushState()
    restoreReplaceState()
    window.removeEventListener('popstate', notify)
  }
}

export default function subscribeToLocationChange(listener: Listener): () => void {
  if (!restore)
    install()
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && restore) {
      restore()
      restore = null
    }
  }
}
